import prettyBytes from 'pretty-bytes';
import { computed, type Ref } from 'vue';

import { useDict, useFischer } from '@/composables/fischerPlugin';

type MediaLibraryItem = {
  fields?: {
    file?: {
      value?: {
        extension?: string;
        src?: string;
        size?: number;
      };
    };
    image?: { value?: { src?: string } };
    title?: { value?: string };
    source?: { value?: string };
  };
};

export const useMediaLibrary = (item: Ref<MediaLibraryItem | undefined>) => {
  const dict = useDict();
  const fischer = useFischer();

  const imageFileTypes = ['jpg', 'png', 'gif', 'ico', 'svg'];

  const fileExtension = computed(() => item.value?.fields?.file?.value?.extension?.toUpperCase());

  const fileUrl = computed(() => item.value?.fields?.file?.value?.src);

  const fileSizeInBytes = computed(() => item.value?.fields?.file?.value?.size);

  const isImageFile = computed(() => {
    if (!fileExtension.value) return false;
    return imageFileTypes.includes(fileExtension.value.toLowerCase());
  });

  const image = computed(() => item.value?.fields?.image?.value?.src);

  const itemTitle = computed(() => item.value?.fields?.title?.value);

  const itemSource = computed(() => item.value?.fields?.source?.value);

  /**
   * The locale has to be the site's, not the runtime's. pretty-bytes routes through
   * `toLocaleString(undefined, options)` as soon as a fraction-digit option is given, so without one
   * the server formats in Node's locale and the browser in the visitor's: `1.3 MB` against `1,3 MB`,
   * a mismatch on every item for anyone whose browser is not set to English. The site language is
   * also the right answer on its own - a German page writes a decimal comma.
   */
  const getFileSize = (bytes: number | string | null | undefined): string => {
    const parsedBytes = Number.parseInt(String(bytes), 10);
    if (!Number.isFinite(parsedBytes)) return '';
    return prettyBytes(parsedBytes, {
      locale: fischer.siteLanguage(),
      maximumFractionDigits: 1,
    });
  };

  const getFileSizeAndLabel = (bytes: number | string | null | undefined): string => {
    const fileSize = getFileSize(bytes);
    if (!fileSize) return '';
    return `${dict.get('media-Library.Items.Size-Label')} ${fileSize}`;
  };

  return {
    fileExtension,
    fileUrl,
    fileSizeInBytes,
    isImageFile,
    image,
    itemTitle,
    itemSource,
    getFileSize,
    getFileSizeAndLabel,
  };
};
